import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'
import type { Session } from '@/types'
import CustomConfirmModal from '../../common/CustomConfirmModal'
import styles from './SharedSettings.module.css'

interface FieldConfig {
    key: string
    label: string
}

const AVAILABLE_FIELDS: FieldConfig[] = [
    { key: 'language', label: 'Idioma' },
    { key: 'category', label: 'Categoría' },
    { key: 'series', label: 'Serie' },
    { key: 'series_number', label: 'Número de series' },
    { key: 'publisher', label: 'Editorial' },
    { key: 'format', label: 'Formato' },
    { key: 'status', label: 'Estado' },
    { key: 'progress', label: 'Progreso' },
    { key: 'location', label: 'Ubicación' },
    { key: 'notes', label: 'Notas' },
    { key: 'created_at', label: 'Fecha de adición' },
    { key: 'synopsis', label: 'Sinopsis' },
    { key: 'page_count', label: 'Nº páginas' },
    { key: 'description', label: 'Descripción' },
]

type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'danger'

interface Notification {
    isOpen: boolean
    title: string
    message: string
    type: NotificationType
}

interface SharedSettingsProps {
    session: Session
}

export default function SharedSettings({ session }: Readonly<SharedSettingsProps>) {
    const { t } = useTranslation()
    const [selectedFields, setSelectedFields] = useState<string[]>(AVAILABLE_FIELDS.map((f) => f.key))
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const [notification, setNotification] = useState<Notification>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    const showNotify = (title: string, message: string, type: NotificationType = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const fetchSettings = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('shared_library_settings')
                .select('visible_fields')
                .eq('user_id', session.user.id)
                .single()

            if (data) {
                const fields = Array.isArray(data.visible_fields) ? data.visible_fields : []
                setSelectedFields(fields)
            } else if (error && error.code !== 'PGRST116') {
                console.error(error)
            }
        } finally {
            setLoading(false)
        }
    }, [session.user.id])

    useEffect(() => {
        fetchSettings()
    }, [fetchSettings])

    const handleSave = async () => {
        setSaving(true)
        try {
            const { error } = await supabase.from('shared_library_settings').upsert({
                user_id: session.user.id,
                visible_fields: selectedFields,
                updated_at: new Date(),
            })

            if (error) throw error
            showNotify('Éxito', t('biblionautas.settings.success'), 'success')
        } catch (e) {
            console.error('Error saving settings:', e)
            showNotify('Error', t('biblionautas.settings.error'), 'danger')
        } finally {
            setSaving(false)
        }
    }

    const toggleField = (key: string) => {
        const newFields = selectedFields.includes(key) ? selectedFields.filter((k) => k !== key) : [...selectedFields, key]

        setSelectedFields(newFields)
    }

    return (
        <div className={styles.sharedSettingsContainer}>
            <div className={styles.settingsHeader}>
                <div style={{ marginBottom: '1.25rem' }}>
                    <p className={styles.helperText}>{t('biblionautas.settings.helperText')}</p>
                </div>
                <button onClick={handleSave} className={styles.btnPrimary} disabled={saving || loading} style={{ minWidth: '160px' }}>
                    {saving ? t('biblionautas.settings.saving') : t('biblionautas.settings.save')}
                </button>
            </div>

            <div className={styles.fieldsGrid}>
                {/* Mandatory Fields */}
                <div className={`${styles.fieldToggle} ${styles.disabled}`}>
                    <label>
                        <input type="checkbox" checked disabled />
                        {t('biblionautas.settings.fields.title')}
                    </label>
                </div>
                <div className={`${styles.fieldToggle} ${styles.disabled}`}>
                    <label>
                        <input type="checkbox" checked disabled />
                        {t('biblionautas.settings.fields.author')}
                    </label>
                </div>

                {/* Optional Fields */}
                {AVAILABLE_FIELDS.map((field) => (
                    <div key={field.key} className={styles.fieldToggle}>
                        <label>
                            <input type="checkbox" checked={selectedFields.includes(field.key)} onChange={() => toggleField(field.key)} />
                            {t(`biblionautas.settings.fields.${field.key}`)}
                        </label>
                    </div>
                ))}
            </div>

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type as 'danger' | 'success' | 'warning' | 'info' | 'confirm'}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })}
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
        </div>
    )
}
